import { useEffect, useRef } from 'react'

import { DIVIDE_BY_TWO } from 'src/Constants/numeric'
import { TikTokCampaignPricingModelEnum } from 'src/Types/campaigns'

const resolvePricingModelSwitch = (
  prevPricingModel: TikTokCampaignPricingModelEnum | null,
  pricingModel?: TikTokCampaignPricingModelEnum | null,
): {
  currentPricingModel: TikTokCampaignPricingModelEnum | null
  isPricingModelSwitched: boolean
} => {
  const currentPricingModel = pricingModel ?? null

  return {
    currentPricingModel,
    isPricingModelSwitched:
      prevPricingModel !== null && prevPricingModel !== currentPricingModel,
  }
}

interface UseSyncTikTokBudgetValuesProps {
  min: number
  max: number
  price: number
  currencyRate: number
  isAudienceRequestNeeded: boolean
  pricingModel?: TikTokCampaignPricingModelEnum | null
  setValueInput: (value: number) => void
  setValueSlider: (value: number) => void
  savePrice: (value: number) => void
}

export const useSyncTikTokBudgetValues = ({
  min,
  max,
  price,
  currencyRate,
  isAudienceRequestNeeded,
  pricingModel,
  setValueInput,
  setValueSlider,
  savePrice,
}: UseSyncTikTokBudgetValuesProps): void => {
  const prevPricingModelRef = useRef(pricingModel ?? null)

  useEffect(() => {
    if (!min || !max) {
      return
    }

    const { currentPricingModel, isPricingModelSwitched } =
      resolvePricingModelSwitch(prevPricingModelRef.current, pricingModel)

    prevPricingModelRef.current = currentPricingModel

    if (!isPricingModelSwitched && !isAudienceRequestNeeded) {
      if (price && currencyRate) {
        setValueInput(Math.round(price * currencyRate))
        setValueSlider(price)
      }

      return
    }

    const targetValue =
      !isPricingModelSwitched && price ? price : (min + max) / DIVIDE_BY_TWO

    setValueSlider(targetValue)
    setValueInput(Math.round(targetValue * currencyRate))
    savePrice(targetValue)
  }, [
    min,
    max,
    price,
    currencyRate,
    isAudienceRequestNeeded,
    pricingModel,
    setValueInput,
    setValueSlider,
    savePrice,
  ])
}
